import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import { getCsrfToken } from "@/providers/auth";
import { z } from "zod";
import { OrderService } from "./order-services";
import type { Paginated } from "./types/orders";
import {
  whapiChannelSchema,
  whapiConnectionStatusSchema,
  whapiGroupActivationSchema,
  whapiGroupsSchema,
  whapiMultimediaMessageSchema,
  whapiThreadsSchema,
} from "./types/whapi";
import {
  type WhatsappChat,
  paginatedWhatsappChatsSchema,
} from "./types/whatsapp-chats";

export type WhatsAppAllowedNumber = {
  phone_number: string;
  name?: string | null;
  type?: string | null;
  id?: string | number | null;
};

export class WhapiService {
  static async getWhatsAppChannel(id?: string) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/channels",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
        },
        whapiChannelSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getWhatsAppChannel] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getWhatsAppChannel] Unexpected error:", error);
      }
      return null;
    }
  }

  static async createWhatsAppChannel({
    whatsappNumber,
    supplierUserId,
  }: {
    whatsappNumber: string;
    supplierUserId: number;
  }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/whatsapp/channels/",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body: {
            phone_number: whatsappNumber,
            supplier_user: supplierUserId,
            is_enabled: true,
            is_universal: false,
          },
        },
        whapiChannelSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[createWhatsAppChannel] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[createWhatsAppChannel] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getWhatsAppConnectionQR(channelId: string) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/channels/{channelId}",
          path: { channelId },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
        },
        z.any(),
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getWhatsAppConnectionQR] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getWhatsAppConnectionQR] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getWhatsAppChatList({
    channelId,
    page,
    pageSize,
  }: {
    channelId: string;
    page: number;
    pageSize: number;
  }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/channels/{channelId}/chats",
          path: { channelId },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          query: { page, page_size: pageSize },
        },
        paginatedWhatsappChatsSchema,
      );
      return data as Paginated<WhatsappChat>;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getWhatsAppConnectionQR] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getWhatsAppConnectionQR] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getWhatsAppConnectionStatus({
    channelId,
  }: { channelId: string }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/channels/{channelId}/status/",
          path: { channelId },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
          },
        },
        whapiConnectionStatusSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getWhatsAppConnectionStatus] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getWhatsAppConnectionStatus] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getWhatsAppAuthCode({
    channelId,
    phoneNumber,
  }: {
    channelId: string;
    phoneNumber: string;
  }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/whatsapp/channels/{channelId}/auth-code/",
          path: { channelId },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body: {
            phone_number: phoneNumber,
          },
        },
        z.object({
          code: z.union([z.string(), z.null()]).optional(),
          raw: z.any().optional(),
          detail: z.string().optional(),
        }),
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getWhatsAppAuthCode] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getWhatsAppAuthCode] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getOrderMessageThread(orderId: string) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/whatsapp-message-threads/",
          query: {
            order_id: orderId,
          },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
        },
        whapiThreadsSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getOrderMessageThread] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getOrderMessageThread] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getGroups() {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/whatsapp-groups/",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
        },
        whapiGroupsSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getGroups] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getGroups] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async updateGroupActivation({
    groupId,
    isActive,
  }: {
    groupId: number;
    isActive: boolean;
  }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "PATCH",
          url: "/api/v1/whatsapp/whatsapp-groups/{groupId}/activation/",
          path: { groupId: groupId.toString() },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body: { is_active: isActive },
        },
        whapiGroupActivationSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[updateGroupActivation] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[updateGroupActivation] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getGroupsWithOrders() {
    try {
      const groups = await WhapiService.getGroups();
      if (!groups) {
        return [];
      }

      const groupsWithOrders = await Promise.all(
        groups.map(async (group) => {
          const enhancedMessages = await Promise.all(
            group.messages?.map(async (message) => {
              if (typeof message?.order === "number") {
                const orderData = await OrderService.getOrderById(
                  message.order.toString(),
                );

                return {
                  ...message,
                  orderData,
                };
              }

              return {
                ...message,
                orderData: null,
              };
            }) || [],
          );

          return {
            ...group,
            messages: enhancedMessages,
          };
        }),
      );

      return groupsWithOrders;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getGroupsWithOrders] API Error during getGroups: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getGroupsWithOrders] Unexpected error:", error);
      }
      return [];
    }
  }

  static async whatsAppChannelAllowNumber({
    channelId,
    phoneNumber,
  }: {
    channelId: number;
    phoneNumber: string;
  }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/whatsapp/channels/allow-number/",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body: {
            channelId: channelId,
            phoneNumber: phoneNumber,
          },
        },
        z.any(),
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[whatsAppAllowPhoneNumber] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[whatsAppAllowPhoneNumber] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async whatsAppChannelDisallowNumber({
    channelId,
    phoneNumber,
  }: {
    channelId: number;
    phoneNumber: string;
  }) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "DELETE",
          url: "/api/v1/whatsapp/channels/allow-number/",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
          body: {
            channelId: channelId,
            phoneNumber: phoneNumber,
          },
        },
        z.any(),
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[whatsAppAllowPhoneNumber] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[whatsAppAllowPhoneNumber] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getWhatsAppChannelAllowedNumbers(
    channelId: number,
  ): Promise<WhatsAppAllowedNumber[]> {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: `/api/v1/whatsapp/channels/allow-number/?channelId=${channelId}`,
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
        },
        z.any(),
      );
      return data as WhatsAppAllowedNumber[];
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[whatsAppAllowPhoneNumber] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[whatsAppAllowPhoneNumber] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getMultimediaMessage(id: number) {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/whatsapp/whatsapp-multimedia-messages/{id}/",
          path: { id: id.toString() },
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
            "Content-Type": "application/json",
          },
        },
        whapiMultimediaMessageSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getMultimediaMessage] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getMultimediaMessage] Unexpected error:", error);
      }
      throw error;
    }
  }
}
